function ___$insertStyle(css) {
    if (!css || typeof window === 'undefined') {
        return;
    }
    const style = document.createElement('style');
    style.setAttribute('type', 'text/css');
    style.innerHTML = css;
    document.head.appendChild(style);
    return css;
}

import addHours from 'date-fns/add_hours';
import addMinutes from 'date-fns/add_minutes';
import addYears from 'date-fns/add_years';
import differenceInDays from 'date-fns/difference_in_days';
import differenceInMonths from 'date-fns/difference_in_months';
import format from 'date-fns/format';
import getMinutes from 'date-fns/get_minutes';
import setMinutes from 'date-fns/set_minutes';
import startOfDay from 'date-fns/start_of_day';
import subMinutes from 'date-fns/sub_minutes';
import isDate from 'lodash/isDate';
import _isNil from 'lodash/isNil';
import _isString from 'lodash/isString';

var MILLISECOND = 1;
var SECOND = 1000 * MILLISECOND;
var MINUTE = 60 * SECOND;
var HOUR = 60 * MINUTE;
var DAY = 24 * HOUR;
var YEAR = 365 * DAY;
var POSITION_SPLIT = /:/;
var DEFAULT_FORMATS = {
  sameDay: '[Today]',
  nextDay: '[Tomorrow]',
  nextWeek: 'dddd',
  lastDay: '[Yesterday]',
  lastWeek: '[Last] dddd',
  sameElse: 'DD/MM/YYYY',
  halfYearOut: 'M/D/YY'
}; // DR: https://github.com/orangemug/date-fns/blob/739718977e00424f4871c0fcb19cdf390108858f/src/calendar/index.js

function calendar(date, referenceTime, formats) {
  if (referenceTime === void 0) {
    referenceTime = new Date();
  }

  if (formats === void 0) {
    formats = DEFAULT_FORMATS;
  }

  var diff = differenceInDays(startOfDay(date), startOfDay(referenceTime));
  var diffInMonths = differenceInMonths(startOfDay(date), startOfDay(referenceTime));
  var formatString;

  if (Math.abs(diffInMonths) > 6) {
    formatString = formats.halfYearOut;
  } else if (diff < -6) {
    formatString = formats.sameElse;
  } else if (diff < -1) {
    formatString = formats.lastWeek;
  } else if (diff < 0) {
    formatString = formats.lastDay;
  } else if (diff < 1) {
    formatString = formats.sameDay;
  } else if (diff < 2) {
    formatString = formats.nextDay;
  } else if (diff < 7) {
    formatString = formats.nextWeek;
  } else {
    formatString = formats.sameElse;
  }

  return format(date, formatString);
}

function positionToSeconds(position) {
  if (!_isString(position)) {
    return 0;
  }

  var parts = position.split(POSITION_SPLIT).map(function (part) {
    return parseInt(part, 10);
  });

  if (parts.length !== 3) {
    return 0;
  } // BC changed for no-mixed-operators


  var part1 = parts[0] * 60 * 60;
  var part2 = parts[1] * 60;
  var part3 = parts[2];
  return part1 + part2 + part3;
}
function secondsToPosition(seconds) {
  if (seconds === void 0) {
    seconds = 0;
  }

  var finiteSeconds = Number.isFinite(seconds) ? seconds : 0;
  var hours = Math.floor(finiteSeconds / 3600);
  var hoursString = hours ? "".concat(hours, ":") : '00:';
  var mins = Math.floor(finiteSeconds / 60 % 60);
  var minString = mins < 10 ? "0".concat(mins, ":") : "".concat(mins, ":");
  var secs = Math.floor(finiteSeconds % 60);
  var secsString = secs < 10 ? "0".concat(secs) : "".concat(secs);
  return hoursString + minString + secsString;
}
function roundMoment(time, interval) {
  var remainder = getMinutes(time) % interval;

  if (remainder >= interval / 2) {
    return addMinutes(time, interval - remainder);
  }

  return subMinutes(time, remainder);
}
function formatDurationInMinutes(duration) {
  if (duration < 60) {
    var roundedDuration = Math.round(duration);
    return "".concat(roundedDuration, "m");
  }

  var minutes = Math.round(duration % 60);
  var hours = Math.floor(duration / 60);

  if (minutes === 0) {
    return "".concat(hours, "h");
  }

  return "".concat(hours, "h ").concat(minutes, "m");
}
var DASH = '–';

function lowerCaseShortMeridiemPeriod(displayTime) {
  var length = displayTime.length;
  return displayTime.substring(0, length - 2) + displayTime.substring(length - 2, length - 1).toLowerCase();
}

function getRoundedTimeFormatPattern(availabilityStartsAt) {
  var startTime = availabilityStartsAt;
  var minutes = getMinutes(startTime);
  var formatPattern = 'h:ma';

  if (minutes < 35 && minutes > 25) {
    startTime = setMinutes(startTime, 30);
  }

  if (minutes < 10 || minutes > 50) {
    formatPattern = 'ha';

    if (minutes > 50) {
      startTime = addHours(startTime, 1);
    }
  }

  return {
    startTime: startTime,
    formatPattern: formatPattern
  };
}

function formatModalListDisplayTime(availabilityStartsAt, referenceTime) {
  if (referenceTime === void 0) {
    referenceTime = new Date();
  }

  if (_isNil(availabilityStartsAt)) {
    return null;
  }

  var _a = getRoundedTimeFormatPattern(availabilityStartsAt),
      formatPattern = _a.formatPattern,
      startTime = _a.startTime;

  var returnedTime = calendar(startTime, referenceTime, {
    sameDay: "[Today], ".concat(formatPattern),
    nextDay: "[Tomorrow], ".concat(formatPattern),
    nextWeek: "dddd, ".concat(formatPattern),
    lastDay: "[Yesterday], ".concat(formatPattern),
    lastWeek: "dddd, ".concat(formatPattern),
    sameElse: "ddd M/D, ".concat(formatPattern),
    halfYearOut: "ddd M/D/YY, ".concat(formatPattern)
  });
  return lowerCaseShortMeridiemPeriod(returnedTime);
}
function formatTileDisplayTime(availabilityStartsAt, referenceTime) {
  if (referenceTime === void 0) {
    referenceTime = new Date();
  }

  if (_isNil(availabilityStartsAt)) {
    return null;
  }

  var _a = getRoundedTimeFormatPattern(availabilityStartsAt),
      formatPattern = _a.formatPattern,
      startTime = _a.startTime;

  var daysFromToday = differenceInDays(startOfDay(startTime), startOfDay(referenceTime));
  var returnedTime = calendar(startTime, referenceTime, {
    sameDay: formatPattern,
    nextDay: "ddd ".concat(formatPattern),
    nextWeek: "ddd ".concat(formatPattern),
    lastDay: "ddd ".concat(formatPattern),
    lastWeek: "ddd ".concat(formatPattern),
    sameElse: "M/D ".concat(formatPattern),
    halfYearOut: "M/D/YY ".concat(formatPattern)
  });
  var formatDateTime = returnedTime;

  if (Math.abs(daysFromToday) < 7) {
    formatDateTime = returnedTime.substring(0, 1).toUpperCase() + returnedTime.substring(1);
  }

  return lowerCaseShortMeridiemPeriod(formatDateTime);
}
function getBroadcastTimeData(_a) {
  var startsAt = _a.startsAt,
      endsAt = _a.endsAt,
      displayTime = _a.displayTime;
  var startsAtDate = isDate(startsAt) ? startsAt : new Date(startsAt);
  var endsAtDate = isDate(endsAt) ? endsAt : new Date(endsAt);
  var durationInMinutes = (+endsAtDate - +startsAtDate) / (1000 * 60);
  var broadcastProgress = (+displayTime - +startsAtDate) / (+endsAtDate - +startsAtDate);
  var broadcastProgressPercent = Math.round(100 * Math.min(Math.max(broadcastProgress, 0), 1));
  return {
    durationInMinutes: durationInMinutes,
    broadcastProgressPercent: broadcastProgressPercent
  };
}
function formatEpisodeAirDate(startsAt) {
  return format(startsAt, 'MMMM D, YYYY');
}
function formatBillDate(date) {
  if (date) {
    return format(date, 'M/D');
  }

  return null;
}
function formatLastServiceDate(date) {
  if (date) {
    return format(date, 'M/D/YY');
  }

  return null;
}
function broadcastDurationInMinutes(broadcast) {
  var startsAtDate = new Date(broadcast.startsAt);
  var endsAtDate = new Date(broadcast.endsAt);
  return (+endsAtDate - +startsAtDate) / (1000 * 60);
}
function presentationDurationInMinutes(_a) {
  var broadcast = _a.broadcast,
      vod = _a.vod;

  if (!_isNil(vod.duration)) {
    return vod.duration / MINUTE;
  }

  if (!_isNil(broadcast.startsAt) && !_isNil(broadcast.endsAt)) {
    return broadcastDurationInMinutes(broadcast);
  }

  return 0;
}
function oneYearFromNow() {
  return addYears(new Date(), 1);
}
function playheadIsActive(_a) {
  var updatedAt = _a.updatedAt,
      displayTime = _a.displayTime;
  var updatedTimestamp = new Date(updatedAt);
  var fiveMinutes = 5 * MINUTE;
  return updatedTimestamp.getTime() >= +displayTime - fiveMinutes;
}
function formatTime(time, formatString) {
  return format(time, formatString);
}

export { DASH, DAY, HOUR, MILLISECOND, MINUTE, SECOND, YEAR, broadcastDurationInMinutes, formatBillDate, formatDurationInMinutes, formatEpisodeAirDate, formatLastServiceDate, formatModalListDisplayTime, formatTileDisplayTime, formatTime, getBroadcastTimeData, oneYearFromNow, playheadIsActive, positionToSeconds, presentationDurationInMinutes, roundMoment, secondsToPosition };
